import { Form, Formik } from 'formik';
import {
  Button,
  Input,
  Modal,
  ModalSettingsShape,
} from '@rabbit/elements/shared-components';
import * as Yup from 'yup';
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { OptionShape, useGetInstallerPeers } from '@rabbit/bizproc/react';
import { useTranslation } from 'react-i18next';
import {
  DTHoldingProxy,
  FBD_Holding_Private,
  FBD_Repairer_Public,
  PersonaTypeSingleLetter,
} from '@rabbit/data/types';
import { toast } from 'react-toastify';
import { CaseflowContext } from '@rabbit/sage/context/CaseflowContext.tsx';

//export interface AssignCaseInternalModalProps {}
interface FormValuesShape {
  installer_id: string;
}

export interface ModalAssignInstallerHoldingProps {
  handleClose: () => void;
  modalSettings: ModalSettingsShape;
  holding_proxy?: DTHoldingProxy | null;
  setInstallerId: Dispatch<SetStateAction<string>>;
  setInstallerLoaded: Dispatch<SetStateAction<boolean>>;
  installerId: string;
  type: string;
}

export function AssignCaseInternalModal({
  handleClose,
  modalSettings,
  holding_proxy,
  setInstallerId,
  setInstallerLoaded,
  installerId,
  type,
}: ModalAssignInstallerHoldingProps) {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { installerPeer, installerPeerLoading } = useGetInstallerPeers();

  const initialValues = {
    installer_id: installerId,
  };

  const { executeAction, moveSpotlight } = useContext(CaseflowContext) || {};
  const installerUsers: OptionShape[] = installerPeerLoading
    ? [
        {
          id: '-1',
          label: `${t('general.loading')}...`,
          value: 'Loading...',
        },
      ]
    : (installerPeer
        .map((installer, index) => {
          const label = installer?.name
            ? `${installer.name}${
                installer.first_name
                  ? ` - ${installer.first_name} ${installer.last_name}`
                  : ''
              }`
            : '';
          const value = installer?.docid || '';
          return {
            id: `${index}`,
            label,
            value,
          };
        })
        .filter(Boolean) as OptionShape[]);

  const [usersForAssignment, setUsersForAssignment] = useState(installerUsers);

  useEffect(() => {
    if (usersForAssignment !== installerUsers)
      setUsersForAssignment(installerUsers);
  }, [installerPeer]);

  const selectedInitialInstallerId = useMemo(() => {
    if (!installerId) return '';
    const exists = usersForAssignment?.some((u) => u.value === installerId);
    return exists ? installerId : '';
  }, [usersForAssignment, installerId]);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        installer_id: Yup.string()
          .trim()
          .required(t('Please select an Installer'))
          .test(
            'changed',
            t('general.installerNotChanged', 'Installer not changed'),
            function (value) {
              // Only enforce change when we actually preselected an existing installer
              if (!selectedInitialInstallerId) return true;
              return value !== selectedInitialInstallerId;
            }
          ),
      }),
    [t, selectedInitialInstallerId]
  );

  const onSubmit = async (values: FormValuesShape) => {
    try {
      const { installer_id } = values;

      if (installer_id === installerId) {
        toast.error(t('message.installerHasNotChanged'));
        return;
      }
      if (installer_id === 'Loading...') {
        toast.error(t('message.pleaseSelectAnInstaller'));
        return;
      }
      if (installer_id === '') {
        toast.error(t('message.pleaseSelectAnInstaller'));
      }
      setIsSubmitting(true);
      if (type === 'HOLDING') {
        const holdingPrivateData = await FBD_Holding_Private.get(
          holding_proxy?.holdingLink!
        );
        const installer = await FBD_Repairer_Public.get(installer_id);
        if (holdingPrivateData) {
          if (holdingPrivateData.installation) {
            holdingPrivateData.installation.installer = installer;
          }
          holdingPrivateData.installer = installer_id;
          await FBD_Holding_Private.set(holdingPrivateData);
        }
      } else {
        const nameForId = usersForAssignment.filter(
          (user) => user.value === values.installer_id
        );
        moveSpotlight &&
          (await moveSpotlight(PersonaTypeSingleLetter.Installer));
        executeAction &&
          (await executeAction('assign_installer_case', {
            delegate_installer_id: installer_id!,
            delegate_installer_name: nameForId[0].label!,
          }));
      }
      setInstallerId(installer_id);
      setIsSubmitting(false);
      setInstallerLoaded(false);
      handleClose();
      toast.success(t('message.installerHasBeenUpdatedSuccessfully'));
    } catch (err) {
      console.log(err);
      toast.error(t('message.somethingWentWrongPleaseTryAgain'));
    }
  };

  const isUpdate = !!holding_proxy?.installer;
  const modalText =
    isUpdate || (installerId && installerId !== 'Unassigned')
      ? t('general.updateInstaller')
      : t('general.assignInstaller');

  return (
    <Modal
      settings={{
        title: modalText,
        headerBackground: true,
        handleClose: handleClose,
      }}
      kind="generic"
      isLoading={isSubmitting}
      className="max-h-[768px] w-full max-w-[724px] overflow-y-auto"
    >
      <Formik
        initialValues={{ installer_id: selectedInitialInstallerId }}
        enableReinitialize
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        {({ values, errors }) => (
          <Form className="mt-5 flex flex-col gap-3 px-4">
            <Input
              type="select"
              label={`${t('general.assignTo')}*`}
              name="installer_id"
              settings={{
                options: usersForAssignment,
                id: 'installer_id',
                placeholder: t('general.select'),
                hint: '*required',
              }}
            />
            <div className="mt-2 flex gap-8">
              <Button
                kind="primary"
                type="submit"
                loading={isSubmitting}
                disabled={values.installer_id === 'Loading...' ? true : false}
              >
                {modalText}
              </Button>
              <Button kind="outline_red" onClick={handleClose}>
                {t('general.cancel')}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}

export default AssignCaseInternalModal;
